import ExcelJS from "exceljs";

export const styleAndProtectDummyRow = async (
  worksheet: ExcelJS.Worksheet,
  dummyRow?: ExcelJS.Row
): Promise<void> => {
  if (dummyRow) {
    dummyRow.eachCell({ includeEmpty: true }, (cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F2F2F2" }, // light grey background
      };
      cell.font = {
        name: "Arial",
        size: 10,
        italic: true,
        color: { argb: "666666" },
      };
    });
  }

  // Format date columns as Text ('@') so Excel won't convert typed YYYY-MM-DD dates into serial numbers
  const headerRow = worksheet.getRow(3);
  if (headerRow) {
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const headerText = String(cell.value ?? "").toLowerCase();
      if (headerText.includes("date") || headerText.includes("time")) {
        const col = worksheet.getColumn(colNumber);
        if (col) {
          col.numFmt = "@";
        }
      }
    });
  }
};


export const cleanUploadedExcelFile = async (
  file: File,
  templateInput?: Record<string, any> | Record<string, any>[]
): Promise<File> => {
  if (!file || !templateInput) {
    return file;
  }

  const templates = Array.isArray(templateInput) ? templateInput : [templateInput];

  const matchesDummy = (rowVals: string[]): boolean => {
    return templates.some((templateObj) => {
      const dummySku = String(templateObj?.sku || templateObj?.SKU || "").trim().toLowerCase();
      const dummyItemName = String(templateObj?.itemName || templateObj?.item_name || "").trim().toLowerCase();

      const rowItemName = String(rowVals[0] ?? "").trim().toLowerCase();
      const rowSku = String(rowVals[1] ?? "").trim().toLowerCase();

      // Only consider as dummy placeholder row if SKU or ItemName is unchanged from sample template placeholders
      const skuIsUnchanged = Boolean(dummySku && rowSku && rowSku === dummySku);
      const itemNameIsUnchanged = Boolean(dummyItemName && rowItemName && rowItemName === dummyItemName);

      return skuIsUnchanged || itemNameIsUnchanged;
    });
  };

  // Handle CSV files
  if (file.name.match(/\.csv$/i)) {
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/);
      if (lines.length < 4) return file;

      // Only check line 4 (index 3) if it matches dummy sample data
      const colsLine4 = (lines[3] || "")
        .split(",")
        .map((c) => c.replace(/^"|"$/g, "").trim().toLowerCase());

      if (matchesDummy(colsLine4)) {
        const cleanedLines = lines.filter((_, idx) => idx !== 3);
        return new File([cleanedLines.join("\n")], file.name, { type: file.type || "text/csv" });
      }
      return file;
    } catch (e) {
      console.error("Error cleaning CSV upload file:", e);
      return file;
    }
  }

  // Handle Excel (XLSX / XLS) files
  if (file.name.match(/\.(xlsx|xls)$/i)) {
    try {
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) return file;

      let modified = false;

      // Only check Row 4 (the designated template sample placeholder row)
      if (worksheet.rowCount >= 4) {
        const row4 = worksheet.getRow(4);
        const row4Vals: string[] = [];
        row4.eachCell({ includeEmpty: true }, (cell) => {
          row4Vals.push(String(cell.value ?? "").trim().toLowerCase());
        });

        if (matchesDummy(row4Vals)) {
          worksheet.spliceRows(4, 1);
          modified = true;
        }
      }

      // Map column headings from Row 3 to detect date columns
      const headers: Record<number, string> = {};
      const headerRow = worksheet.getRow(3);
      if (headerRow) {
        headerRow.eachCell({ includeEmpty: true }, (cell, colIdx) => {
          headers[colIdx] = String(cell.value ?? "").toLowerCase();
        });
      }

      // Normalize date cells across all remaining data rows (Row 4 onwards)
      for (let r = 4; r <= worksheet.rowCount; r++) {
        const row = worksheet.getRow(r);
        row.eachCell({ includeEmpty: true }, (cell, colIdx) => {
          const colName = headers[colIdx] || "";
          const isDateCol = colName.includes("date") || colName.includes("time") || colName.includes("dob");

          // Case A: Cell is a JS Date object
          if (cell.value instanceof Date || cell.type === ExcelJS.ValueType.Date) {
            const val = cell.value instanceof Date ? cell.value : new Date(String(cell.value));
            if (!isNaN(val.getTime())) {
              const safeDate = new Date(val.getTime() + 12 * 60 * 60 * 1000); // add 12 hours to prevent timezone shift across midnight
              const year = safeDate.getUTCFullYear();
              const month = String(safeDate.getUTCMonth() + 1).padStart(2, "0");
              const day = String(safeDate.getUTCDate()).padStart(2, "0");
              cell.value = `${year}-${month}-${day}`;
              cell.numFmt = "@";
              modified = true;
            }
          }
          // Case B: Stored as a number (Excel serial date number, e.g. 46218 for 2026-07-15)
          else if (typeof cell.value === "number" && cell.value > 30000 && cell.value < 100000 && isDateCol) {
            const excelEpoch = new Date(Date.UTC(1899, 11, 30));
            const safeDate = new Date(excelEpoch.getTime() + cell.value * 86400000 + 12 * 3600000);
            if (!isNaN(safeDate.getTime())) {
              const year = safeDate.getUTCFullYear();
              const month = String(safeDate.getUTCMonth() + 1).padStart(2, "0");
              const day = String(safeDate.getUTCDate()).padStart(2, "0");
              cell.value = `${year}-${month}-${day}`;
              cell.numFmt = "@";
              modified = true;
            }
          }
          // Case C: Formatted string with slashes or dots (DD/MM/YYYY or MM/DD/YYYY)
          else if (typeof cell.value === "string" && isDateCol) {
            const strVal = cell.value.trim();
            const slashMatch = strVal.match(/^(\d{1,2})[\/\.-](\d{1,2})[\/\.-](\d{4})$/);
            if (slashMatch) {
              const p1 = parseInt(slashMatch[1], 10);
              const p2 = parseInt(slashMatch[2], 10);
              const year = slashMatch[3];
              let day = p1;
              let month = p2;
              if (p1 > 12) {
                day = p1;
                month = p2;
              } else if (p2 > 12) {
                month = p1;
                day = p2;
              }
              const formattedMonth = String(month).padStart(2, "0");
              const formattedDay = String(day).padStart(2, "0");
              cell.value = `${year}-${formattedMonth}-${formattedDay}`;
              cell.numFmt = "@";
              modified = true;
            }
          }
        });
      }

      if (!modified) {
        return file;
      }

      const buffer = await workbook.xlsx.writeBuffer();
      return new File([buffer], file.name, { type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    } catch (err) {
      console.error("Error cleaning upload file:", err);
      return file;
    }
  }

  return file;
};
